import React from 'react';
import { Cloud, Package, Server, Database, HardDrive, Globe } from 'lucide-react';

export default function ServiciosPorNubeTable({ 
  servicios = [],
  nubesLista = [],
  selectedNube, 
  onNubeChange,
  totalesNube = {},  // NUEVO: totales desde backend con oportunidades únicas
  isDarkMode = true 
}) {
  const cardClasses = isDarkMode
    ? 'bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 shadow-lg'
    : 'bg-white border border-gray-200 rounded-xl p-6 shadow-lg';

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';

  // Usar totales desde el prop (vienen del backend con COUNT(DISTINCT oportunidad))
  const totalServicios = totalesNube.total_servicios || servicios.length;
  const totalOportunidadesUnicas = totalesNube.oportunidades_unicas || 0;
  
  // IMPORTANTE: NO sumar servicios[].oportunidades porque eso duplica
  // (una oportunidad puede tener múltiples servicios)
  // Ahora usamos totalesNube.oportunidades_unicas que ya viene calculado del backend

  // Función para obtener el ícono según el proveedor (usando lucide-react)
  const getNubeIcon = (nubeServicio) => {
    const nubeNormalizada = nubeServicio?.toLowerCase() || '';
    
    if (nubeNormalizada.includes('aws') || nubeNormalizada.includes('amazon')) {
      return Server; // AWS
    }
    if (nubeNormalizada.includes('azure')) {
      return Cloud; // Azure
    }
    if (nubeNormalizada.includes('gcp') || nubeNormalizada.includes('google cloud')) {
      return Database; // GCP
    }
    if (nubeNormalizada.includes('365') || nubeNormalizada.includes('office')) {
      return Package; // Microsoft 365
    }
    if (nubeNormalizada.includes('workspace') || nubeNormalizada.includes('google')) {
      return Globe; // Google Workspace
    }
    if (nubeNormalizada.includes('huawei')) {
      return HardDrive; // Huawei
    }
    
    // Fallback
    return Cloud;
  };

  // Función para obtener colores por nube (colores oficiales de cada proveedor)
  const getNubeColor = (nubeServicio) => {
    const nubeNormalizada = nubeServicio?.toLowerCase() || '';
    
    // Diccionario de colores oficiales por proveedor
    const coloresNube = {
      // AWS - Naranja oficial
      'aws': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
      'amazon': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
      'amazon aws': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
      
      // Microsoft Azure - Azul oficial
      'azure': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      'microsoft azure': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      
      // Microsoft 365 - Verde oficial
      '365': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      'microsoft 365': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      'office 365': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      
      // Google GCP - Azul Google
      'gcp': { bg: 'bg-blue-600/20', text: 'text-blue-300', border: 'border-blue-600/30' },
      'google gcp': { bg: 'bg-blue-600/20', text: 'text-blue-300', border: 'border-blue-600/30' },
      'google cloud': { bg: 'bg-blue-600/20', text: 'text-blue-300', border: 'border-blue-600/30' },
      
      // Google Workspace - Rojo/Multicolor Google
      'workspace': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
      'google workspace': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
      
      // Huawei - Rojo oficial
      'huawei': { bg: 'bg-red-600/20', text: 'text-red-400', border: 'border-red-600/30' },
      'huawei cloud': { bg: 'bg-red-600/20', text: 'text-red-400', border: 'border-red-600/30' },
      
      // Alibaba - Naranja
      'alibaba': { bg: 'bg-orange-600/20', text: 'text-orange-400', border: 'border-orange-600/30' },
      'alibaba cloud': { bg: 'bg-orange-600/20', text: 'text-orange-400', border: 'border-orange-600/30' },
      
      // Oracle - Rojo
      'oracle': { bg: 'bg-red-700/20', text: 'text-red-400', border: 'border-red-700/30' },
      'oracle cloud': { bg: 'bg-red-700/20', text: 'text-red-400', border: 'border-red-700/30' },
      
      // IBM - Azul oscuro
      'ibm': { bg: 'bg-blue-900/20', text: 'text-blue-300', border: 'border-blue-900/30' },
      'ibm cloud': { bg: 'bg-blue-900/20', text: 'text-blue-300', border: 'border-blue-900/30' },
      
      // Digital Ocean - Azul claro
      'digitalocean': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
      'digital ocean': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
      
      // Default - Gris
      'ninguna': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
      'other': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
    };
    
    // Buscar coincidencia en el diccionario
    for (const [key, colors] of Object.entries(coloresNube)) {
      if (nubeNormalizada.includes(key)) {
        return colors;
      }
    }
    
    // Color por defecto si no se encuentra
    return { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' };
  };

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg flex items-center justify-center border-2 ${
            selectedNube 
              ? (() => {
                  const colors = getNubeColor(selectedNube);
                  return `${colors.bg} ${colors.border}`;
                })()
              : 'bg-gradient-to-br from-blue-500 to-blue-600 border-blue-400'
          }`}>
            {(() => {
              const IconComponent = selectedNube ? getNubeIcon(selectedNube) : Cloud;
              const colors = selectedNube ? getNubeColor(selectedNube) : null;
              return <IconComponent className={`w-8 h-8 ${colors ? colors.text : 'text-white'}`} />;
            })()}
          </div>
          <div>
            <h3 className={`${textPrimary} text-xl font-bold`}>Servicios por Nube</h3>
            <p className={`${textSecondary} text-sm`}>
              {totalServicios} tipos de servicio • {totalOportunidadesUnicas} oportunidades únicas
            </p>
            <p className={`${textSecondary} text-xs italic`}>
              Clientes únicos por servicio específico (no por nube total)
            </p>
          </div>
        </div>

        {/* Selector de Nube - sin opción "Todas" */}
        <select
          value={selectedNube || ''}
          onChange={(e) => onNubeChange(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${
            isDarkMode
              ? 'bg-white/5 border-white/10 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          } focus:outline-none focus:ring-2 focus:ring-[#00ffb3]`}
        >
          {nubesLista.map((nube) => (
            <option key={nube.nube_servicio} value={nube.nube_servicio}>
              {nube.nube_servicio} ({nube.total_servicios})
            </option>
          ))}
        </select>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        {servicios.length === 0 ? (
          <div className={`${textSecondary} text-center py-8`}>
            No hay servicios disponibles
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-inherit">
              <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <th className={`${textSecondary} text-left py-2 px-3 font-medium text-xs`}>
                  Servicio
                </th>
                <th className={`${textSecondary} text-left py-2 px-3 font-medium text-xs`}>
                  Nube
                </th>
                <th className={`${textSecondary} text-center py-2 px-3 font-medium text-xs`}>
                  Líneas
                </th>
                <th className={`${textSecondary} text-center py-2 px-3 font-medium text-xs`}>
                  Clientes Únicos
                </th>
              </tr>
            </thead>
            <tbody>
              {servicios.map((servicio, index) => {
                const nubeColors = getNubeColor(servicio.nube_servicio);
                return (
                  <tr
                    key={index}
                    className={`border-b ${
                      isDarkMode 
                        ? 'border-white/5 hover:bg-white/5' 
                        : 'border-gray-100 hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <td className={`${textPrimary} py-2 px-3 font-medium text-sm`}>
                      {servicio.servicio || 'N/A'}
                    </td>
                    <td className={`${textSecondary} py-2 px-3 text-sm`}>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${nubeColors.bg} ${nubeColors.text} ${nubeColors.border}`}>
                        {servicio.nube_servicio || 'N/A'}
                      </span>
                    </td>
                    <td className={`${textPrimary} py-2 px-3 text-center font-semibold text-sm`}>
                      {servicio.oportunidades || 0}
                    </td>
                    <td className={`${textSecondary} py-2 px-3 text-center text-sm`}>
                      {servicio.clientes_unicos || 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}